/**
 * Safari/iOS location-permission sheet.
 *
 * Safari gives a web page no way to re-ask for location once the user said no:
 * `watchPosition` returns code 1 forever. The only fix is a Settings change,
 * so a one-line HUD message leaves the fan stuck with no path out. This module
 * owns the rule (`shouldOfferSettings`) and the copy; `ar_hook.js` owns the DOM.
 *
 * The prompt is a modal, not a `window.confirm`: Safari does not style those,
 * and a modal can carry the step list plus a link the user can tap.
 */

/** `deep-link`: iOS understands `App-Prefs:` on many releases (Settings root).
 *  `steps`: the user opens Settings by hand (every release). */
export const GEO_DENIED_SETTINGS_URL = "App-Prefs:Privacy&path=LOCATION"

/** Only iOS Safari is offered the sheet: Chrome and Firefox show a permission
 *  icon in the address bar, and Android apps are not involved. */
export function isIosSafari(nav = globalThis.navigator) {
  const ua = String(nav?.userAgent || "")
  if (!/iPhone|iPad|iPod/.test(ua)) return false
  // Every iOS browser reports "Safari" in the UA string, but only Safari (and
  // Home Screen web apps) has no CriOS/FxiOS/EdgiOS token.
  return !/CriOS|FxiOS|EdgiOS|OPiOS|mercury/i.test(ua)
}

/**
 * Whether to show the Settings sheet.
 *
 * `code === 1` is PERMISSION_DENIED. A dismissal sticks for this page, because
 * the fan has read the steps and is going to Settings; only a fresh Start
 * attempt (`retried`) is a new question worth asking.
 */
export function shouldOfferSettings({code, iosSafari, dismissedAt, retried = false}) {
  if (!iosSafari) return false
  if (code !== 1) return false
  // A dismissal sticks for this page: the fan has read the steps and is going
  // to Settings. Re-opening on the next watch error would fight them.
  if (!dismissedAt) return true
  // One exception: they came back and hit Start again and it still failed.
  // That is a fresh attempt, not the same error replayed.
  return Boolean(retried)
}
